import React, { useEffect, useState } from 'react';

import { sectionEmptyLabel, sectionModule, sectionSummary, sectionTitle } from './homeSectionSummary';

import Alert from 'CommonComponents/Alert';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import useApiRequest from 'Hooks/useApiRequest';

import ArrowLeftIcon from 'Assets/icons/arrow-left.svg';
import ArrowRightIcon from 'Assets/icons/arrow-right.svg';

/* "Forward" is leftward in Arabic, so the link arrow swaps glyph rather than
   always pointing right. `dir` is set on the root element by
   layouts/app.blade.php, which is what makes this read reliable at mount. */
const isRtl = () => typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

/**
 * The DOM id of the one detail panel.
 *
 * Shared with whatever opens a list from outside it — a tile, an attention card
 * — so it can scroll to and focus the panel it has just changed.
 */
export const PANEL_ID = 'home-detail-panel';

/**
 * The card every Home detail list is drawn in: heading, a summary line, the
 * body, and a footer.
 *
 * ONE SHAPE FOR ALL SEVEN LISTS, approvals included. The approvals list is
 * fetched from a different endpoint and rendered by different row components,
 * but it is the same thing on the page — a list opened by something above it —
 * and a second card style for it would read as a second kind of surface.
 */
export function HomePanelCard({ title, summary, footer, children }) {
    return (
        <section
            id={PANEL_ID}
            tabIndex={-1}
            aria-labelledby={`${PANEL_ID}-title`}
            className="tw-rounded-lg tw-bg-white tw-px-6 tw-py-5 tw-shadow-primary focus:tw-outline-none"
        >
            <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-x-3 tw-gap-y-1">
                <h3 id={`${PANEL_ID}-title`} className="tw-mb-0 tw-text-base tw-font-semibold tw-text-charcoal">
                    {title}
                </h3>
                {!!summary && <p className="tw-mb-0 tw-text-xs tw-text-charcoal-60">{summary}</p>}
            </div>

            {children}

            {!!footer && (
                <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-grey tw-pt-3 tw-text-xs tw-text-charcoal-60">
                    {footer}
                </div>
            )}
        </section>
    );
}

/** The "View all in Procurement" style link every panel footer ends with. */
export function ViewAllLink({ href, module }) {
    const ArrowIcon = isRtl() ? ArrowLeftIcon : ArrowRightIcon;

    return (
        <a href={href} className="tw-inline-flex tw-items-center tw-gap-x-1 tw-text-xs tw-font-semibold tw-text-blue tw-no-underline">
            {$t('View all in {{module}}', { module })}
            <ArrowIcon className="tw-w-3.5 tw-h-3.5" />
        </a>
    );
}

/**
 * The open detail list for one work type.
 *
 * THERE IS NO COLLAPSE AND NO TOGGLE. Exactly one list is open at a time and
 * the caller decides which, so a section is either the open one — in which case
 * it is mounted, fetched and shown — or it is not mounted at all. The `+`
 * controls that used to expand eight stacked sections independently are gone
 * with the stack itself.
 *
 * THE ROWS ARE RE-FETCHED WHEN "Show closed" IS TOGGLED, not filtered on the
 * client. The server's list is capped at its own row limit, so a closed record
 * that belongs in the top ten by urgency is not in the payload to be revealed —
 * it has to be asked for.
 */
export default function HomeSection({ type, count, deltas, sectionUrl, refreshToken = 0, children }) {
    const rows = useApiRequest({ url: sectionUrl, displayErrorToast: false });

    /* Reset by the `type` effect below rather than held per type: a user who
       revealed closed contracts and then opened risks should not find risks
       already widened on their behalf. */
    const [includeClosed, setIncludeClosed] = useState(false);

    const section = rows.data?.data;

    useEffect(() => {
        setIncludeClosed(false);
        rows.get();
    }, [type]);

    /* A row was actioned in the detail pop-up, so what is on screen is now out
       of date. RE-READ RATHER THAN PATCH: the status the server wrote is what it
       decided to write, not necessarily what was submitted, and the same
       response can change what the user may do to the record next.

       Skipped on mount — the token starts at 0 and the effect above has just
       fetched. */
    useEffect(() => {
        if (refreshToken) {
            rows.get(includeClosed ? { include_closed: 1 } : undefined);
        }
    }, [refreshToken]);

    const showClosed = (next) => {
        setIncludeClosed(next);
        rows.get(next ? { include_closed: 1 } : undefined);
    };

    /* THE HEADING FOLLOWS THE FOOTER ONCE THE LIST IS WIDENED, and only then.
       `count` is the tile's figure off the entitlement payload, which is never
       widened; `section.count` is this response's own, which is. Reading the
       first while the footer reads the second put "1 contract" over "Showing 3
       of 3".

       NARROW ON PURPOSE — `section?.count ?? count` would be equivalent today
       only because the two are the same builder, an identity the suite pins for
       risks, procurements and contracts but not for the other five types. This
       form is byte-for-byte the old behaviour whenever includeClosed is false,
       so it cannot regress an unpinned type, and it keeps the page-load figure
       during the stale-count case rather than dropping the heading to 0.

       THE COST IS A TRANSIENT ON THE WAY OUT, and it is known rather than
       overlooked. Hiding closed items flips includeClosed on the click while
       useApiRequest keeps the previous payload mounted for the round trip, so
       the heading drops to the un-widened figure a beat before the footer
       follows — "1 contract" over "Showing 3 of 3" again, briefly, in the
       opposite direction. Tracking the payload instead would remove it, at the
       price of a permanently possible wrong number on the five unpinned types.
       A sub-second transient leaving an opt-in view is the cheaper of the two. */
    const displayCount = includeClosed && section ? section.count : count;

    const summary = sectionSummary(type, displayCount, deltas);

    const subHeader = (
        <>
            {!!summary.exception && (
                <>
                    <span className="tw-font-semibold tw-text-red">{summary.exception}</span>
                    <span aria-hidden="true"> · </span>
                </>
            )}
            {summary.detail}
        </>
    );

    /* DRIVEN OFF `closed_count`, NEVER OFF A LIST OF TYPES HELD HERE. Three of
       the six work types have nothing a "closed" filter could be hiding —
       R.A.D.I.O. statuses are event-configurable free text with no open/closed
       flag, and a procurement count carries no status filter at all — so the
       server reports a structural 0 for them and the link simply never appears.
       A per-type list on this side would be the same decision written a second
       time, in the place least able to see whether it is still true. */
    const hasClosed = (section?.closed_count ?? 0) > 0;

    const footer = !!section && (
        <>
            <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
                <span>{$t('Showing {{shown}} of {{total}}', { shown: section.rows.length, total: section.count })}</span>
                {hasClosed && (
                    <button
                        type="button"
                        onClick={() => showClosed(!includeClosed)}
                        className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-text-blue"
                    >
                        {includeClosed
                            ? $t('Hide closed')
                            : $t('Show closed ({{count}})', {
                                  count: section.closed_count,
                              })}
                    </button>
                )}
            </span>
            <ViewAllLink href={section.view_all_url} module={sectionModule(type)} />
        </>
    );

    return (
        <HomePanelCard title={sectionTitle(type)} summary={subHeader} footer={footer}>
            {rows.loading && <LoadingSpinner />}

            {!!rows.error && !rows.loading && <Alert type="error">{$t('We could not load this list. Please refresh to try again.')}</Alert>}

            {/* TWO DIFFERENT EMPTY STATES, TOLD APART BY THE SERVER'S OWN COUNT,
                which is taken from the same query as the rows in the same
                response — so the two figures agreeing or not is a fact about
                this payload rather than a guess.

                `count === 0` is the ordinary answer a tile at zero opens on: the
                user holds none of these, which they asked to be told.

                `count > 0` with no rows is the stale case — the count arrived
                with the page, the rows arrived after it, and something was
                deleted in between. Only that one tells the user to refresh,
                because only that one is fixed by refreshing.

                Neither renders the table, so column headers never sit over an
                empty body. */}
            {!!section && !rows.loading && (section.rows.length === 0 ? <EmptyList type={type} count={section.count} /> : children(section.rows))}
        </HomePanelCard>
    );
}

function EmptyList({ type, count }) {
    return (
        <p className="tw-mb-0 tw-py-6 tw-text-center tw-text-xs tw-text-charcoal-60">
            {count === 0 ? sectionEmptyLabel(type) : $t('These items are no longer available. Refresh to see the current list.')}
        </p>
    );
}

/**
 * The control that opens one list row's detail pop-up.
 *
 * A REAL <button>, NOT A CLICKABLE ROW ALONE. A row no longer navigates out of
 * Home — it opens a pop-up — and the whole row carries an onClick for the
 * convenience of a mouse. But a <tr> cannot be made a keyboard control without
 * `role="button"`, which would destroy the table semantics a screen reader
 * navigates by. So the row keeps ONE focusable control, and this is it:
 * keyboard and assistive-tech users reach every row through it, and the row's
 * own onClick is pure convenience layered on top.
 *
 * IT IS THE ROW'S FIRST CELL NOW, not a trailing "Open" column. The column was
 * a sixth of the table's width spent on a word, on a table where every row
 * opens — so the control moved onto the text that names the record, which is
 * what a user aims at anyway. Deleting it outright is what must not happen: it
 * is the only focusable thing in the row.
 *
 * It stops propagation because the row's handler would otherwise fire a second
 * time for the same click.
 */
export function OpenRowButton({ onClick, label, children, className = 'tw-text-start tw-text-charcoal' }) {
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onClick?.();
            }}
            aria-label={label}
            className={`tw-border-0 tw-bg-transparent tw-p-0 tw-text-0.5sm tw-font-medium ${className}`}
        >
            {children ?? $t('Open')}
        </button>
    );
}

/**
 * Home's exception treatment for a row: a red rule down its leading edge.
 *
 * APPLIED TO THE ROW'S FIRST CELL, not to the row. It replaces the full-row
 * `tw-bg-red-10` wash, which on a list where a third of the rows are late
 * turned the table into a block of colour and stopped marking anything. `!`
 * because `.inbud-table` sets its own cell borders.
 */
export const EXCEPTION_CELL_CLASS = '!tw-border-s-[3px] !tw-border-s-red';

/**
 * The full-row wash, kept for the My approvals REGISTER alone.
 *
 * Not for the lists in this panel — they use EXCEPTION_CELL_CLASS above. The
 * register is a filtered, paged table a user goes to deliberately and reads a
 * row at a time, where the wash still marks one row against its neighbours;
 * this panel is a capped top-ten of the most urgent work there is, where by
 * construction most rows are the exception and the wash marked nothing. `!`
 * because `.inbud-table` sets its own row background.
 */
export const OVERDUE_ROW_CLASS = '!tw-bg-red-10';

/** What a blank cell says, rather than leaving the column looking broken. */
export function NotSet() {
    return <span className="tw-italic tw-text-charcoal-50">{$t('Not set')}</span>;
}

/** A cell's value, or the "Not set" placeholder when it is blank. */
export const orNotSet = (value) => (value === null || value === undefined || value === '' ? <NotSet /> : value);
